/**
 * NFC Solutions — Unified slip printing (Session 30.7)
 * ─────────────────────────────────────────────────────────────────
 *
 * One call — `printSlip(receipt)` — and the right transport is picked
 * for the shell the app is running in:
 *
 *   • Android APK (Capacitor)  → Bluetooth ESC/POS (silent) via the
 *                                `NfcsPrinter` plugin when a printer is
 *                                selected; otherwise the Android system
 *                                print sheet with the HTML receipt.
 *   • Windows EXE (Tauri)      → raw ESC/POS to the Windows spooler
 *                                (silent, no dialog) via `print_raw_receipt`.
 *   • Browser / PWA            → self-contained iframe + window.print()
 *                                (silent only with --kiosk-printing).
 *
 * The receipt data shape is shared with `buildReceiptHtml` in
 * thermalPrint.js so every dashboard passes the same object:
 *   { business:{name,address,phone,vat_number}, tableLabel, orderLabel,
 *     header, dateStr, servedBy, lines:[{qty,name,price,modifiers[],voided}],
 *     subtotal, vatRate, vatAmount, voucherDiscount, voucherLabel, total,
 *     payments:[{label,amount}], tip, changeDue, footer, formatPrice }
 */

import { buildReceiptHtml, printReceipt } from "@/lib/thermalPrint";

export const PRINTER_CONFIG_KEY = "nfcs:printer:config";
export const POWERED_BY = "Powered by NFC Solutions";

const DEFAULT_CONFIG = {
  paperWidth: 80,      // 80 | 58 (mm)
  btAddress: "",       // Android: selected Bluetooth printer MAC
  btName: "",
  winPrinterName: "",  // Windows: "" = OS default printer
  copies: 1,
  cut: true,
};

// ── Config persistence ─────────────────────────────────────────────
export function getPrinterConfig() {
  try {
    const raw = window.localStorage.getItem(PRINTER_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...DEFAULT_CONFIG, ...(parsed || {}) };
  } catch (_) {
    return { ...DEFAULT_CONFIG };
  }
}

export function savePrinterConfig(patch) {
  const next = { ...getPrinterConfig(), ...(patch || {}) };
  try { window.localStorage.setItem(PRINTER_CONFIG_KEY, JSON.stringify(next)); } catch (_) { /* noop */ }
  return next;
}

// ── Shell detection ────────────────────────────────────────────────
function _capacitorPrinter() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return null;
    if (cap.getPlatform && cap.getPlatform() !== "android") return null;
    return (cap.Plugins || {}).NfcsPrinter || null;
  } catch (_) { return null; }
}

let _tauriInvoke = null;
async function _tauri() {
  if (_tauriInvoke !== null) return _tauriInvoke || null;
  try {
    const w = typeof window !== "undefined" ? window : null;
    if (!w || (!w.__TAURI_INTERNALS__ && !w.__TAURI__ && !w.isTauri)) { _tauriInvoke = false; return null; }
    const mod = await import("@tauri-apps/api/core").catch(() => null);
    _tauriInvoke = mod && typeof mod.invoke === "function" ? mod.invoke : false;
    return _tauriInvoke || null;
  } catch (_) { _tauriInvoke = false; return null; }
}

/** "android" | "windows" | "browser" — which printing path applies here. */
export async function detectPrintShell() {
  if (_capacitorPrinter()) return "android";
  if (await _tauri()) return "windows";
  return "browser";
}

// ── ESC/POS byte builder ───────────────────────────────────────────
const ESC = 0x1b;
const GS = 0x1d;

// Thermal printers default to code page 437 — map the handful of
// typographic characters we use to plain ASCII so nothing prints as
// garbage on cheap firmware.
function _ascii(s) {
  return String(s == null ? "" : s)
    .replace(/×/g, "x").replace(/[—–]/g, "-").replace(/·/g, "-")
    .replace(/[“”]/g, "\"").replace(/[‘’]/g, "'").replace(/…/g, "...")
    .replace(/✓/g, "*").replace(/\u00a0/g, " ")
    .replace(/[^\x20-\x7e\n]/g, "?");
}

class EscPos {
  constructor(cols) { this.cols = cols; this.buf = []; this.baseBold = true; }
  raw(...bytes) { for (const b of bytes) this.buf.push(b & 0xff); return this; }
  text(s) { for (const ch of _ascii(s)) this.buf.push(ch.charCodeAt(0)); return this; }
  line(s = "") { return this.text(s).raw(0x0a); }
  /**
   * Session 30.13 — DARK PRINT. Thermal slips were coming out faint, so
   * every slip now enables emphasized mode (ESC E 1) AND double-strike
   * (ESC G 1) for the whole document — both are universal ESC/POS
   * commands that make the head burn each dot twice/heavier. Text that
   * was "bold" before is now printed with the same weight as the rest,
   * and importance is signalled with size instead.
   */
  init() {
    return this.raw(ESC, 0x40)            // ESC @  reset
      .raw(ESC, 0x74, 0x00)               // code page 437
      .raw(ESC, 0x47, 0x01)               // ESC G 1 double-strike ON
      .raw(ESC, 0x45, 0x01);              // ESC E 1 emphasized ON
  }
  align(a) { return this.raw(ESC, 0x61, a === "center" ? 1 : a === "right" ? 2 : 0); }
  // Never drop below the document's base emphasis.
  bold(on) { return this.raw(ESC, 0x45, (on || this.baseBold) ? 1 : 0); }
  size(w = 1, h = 1) { return this.raw(GS, 0x21, ((Math.min(Math.max(w, 1), 8) - 1) << 4) | (Math.min(Math.max(h, 1), 8) - 1)); }
  feed(n = 1) { return this.raw(ESC, 0x64, Math.min(Math.max(n, 0), 255)); }
  cut() { return this.raw(GS, 0x56, 0x42, 0x00); }                    // partial cut with feed
  divider(ch = "-") { return this.line(ch.repeat(this.cols)); }
  /** left ....... right, padded to the column count, wrapping left if needed. */
  row(left, right, { bold = false, indent = 0 } = {}) {
    const r = _ascii(right);
    const width = this.cols - r.length - 1;
    const lines = _wrap(_ascii(left), Math.max(8, width - indent)).map((l) => " ".repeat(indent) + l);
    if (bold) this.bold(true);
    lines.forEach((l, i) => {
      if (i === lines.length - 1) this.line(l.padEnd(this.cols - r.length) + r);
      else this.line(l);
    });
    if (bold) this.bold(false);
    return this;
  }
  bytes() { return Uint8Array.from(this.buf); }
}

function _wrap(text, width) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const out = []; let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= width) cur += " " + w;
    else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  // Hard-break any single word longer than the width.
  return out.flatMap((l) => (l.length <= width ? [l] : l.match(new RegExp(`.{1,${width}}`, "g"))));
}

/**
 * Build the ESC/POS byte stream for a receipt. `paperWidth` 80 → 48
 * columns (Font A), 58 → 32 columns.
 */
export function buildEscPosReceipt(data, { paperWidth = 80, cut = true } = {}) {
  const cols = Number(paperWidth) === 58 ? 32 : 48;
  const d = data || {};
  const money = (n) => (typeof d.formatPrice === "function" ? d.formatPrice(Number(n) || 0) : `R ${(Number(n) || 0).toFixed(2)}`);
  const p = new EscPos(cols).init();

  // ── Header: big business name, then meta lines ──
  p.align("center").bold(true).size(2, 2).line(d.business?.name || "Receipt").size(1, 1).bold(false);
  if (d.business?.address) p.line(d.business.address);
  if (d.business?.phone) p.line(d.business.phone);
  if (d.business?.vat_number) p.line(`VAT No: ${d.business.vat_number}`);
  p.feed(1);
  if (d.tableLabel) p.bold(true).size(1, 2).line(d.tableLabel).size(1, 1).bold(false);
  if (d.orderLabel) p.bold(true).line(d.orderLabel).bold(false);
  if (d.header) p.line(d.header);
  p.line(d.dateStr || new Date().toLocaleString());
  if (d.servedBy) p.line(`Served by ${d.servedBy}`);
  p.align("left").divider("=");

  // ── Items ──
  const lines = Array.isArray(d.lines) ? d.lines : [];
  if (!lines.length) p.align("center").line("(no items)").align("left");
  for (const ln of lines) {
    const isHeading = (ln.qty === "" || ln.qty == null) && (ln.price === "" || ln.price == null);
    if (isHeading) { p.align("center").bold(true).line(ln.name || "").bold(false).align("left"); continue; }
    const qty = ln.qty || 1;
    const label = `${qty} x ${ln.name || ""}${ln.voided ? " (VOID)" : ""}`;
    p.size(1, 2).row(label, money(ln.price || 0), { bold: true }).size(1, 1);
    for (const m of (ln.modifiers || []).filter(Boolean)) p.row(`- ${m}`, "", { indent: 4 });
  }
  p.divider("-");

  // ── Totals ──
  p.row("Subtotal", money(d.subtotal || 0));
  if (d.vatRate != null && Number(d.vatRate) > 0) {
    const vat = d.vatAmount != null ? d.vatAmount : (Number(d.subtotal || 0) - Number(d.subtotal || 0) / (1 + Number(d.vatRate) / 100));
    p.row(`VAT (${d.vatRate}% incl.)`, money(vat));
  }
  if (Number(d.voucherDiscount) > 0) p.row(`Voucher ${d.voucherLabel || ""}`.trim(), `-${money(d.voucherDiscount)}`);
  // Session 34 — automatic service fee (gratuity) line.
  if (Number(d.serviceFeeAmount) > 0) p.row(`Service fee (${d.serviceFeePct || 0}%)`, money(d.serviceFeeAmount));
  p.divider("-");
  p.bold(true).size(1, 2).row("TOTAL", money(d.total || 0)).size(1, 1).bold(false);

  const pays = (d.payments || []).filter((x) => Number(x.amount) > 0);
  const gratuityTotal = Math.round((Number(d.serviceFeeAmount || 0) + Number(d.tip || 0)) * 100) / 100;
  if (pays.length || Number(d.tip) > 0 || Number(d.changeDue) > 0) {
    p.divider("-");
    for (const pay of pays) p.row(pay.label || "Paid", money(pay.amount));
    if (Number(d.tip) > 0) p.row(Number(d.serviceFeeAmount) > 0 ? "Extra tip" : "Tip", money(d.tip));
    if (Number(d.changeDue) > 0) p.bold(true).row("Change", money(d.changeDue)).bold(false);
  }
  // Session 35 — who the service fee + tip go to (waiter who opened the table).
  if (d.gratuityRecipient && gratuityTotal > 0) {
    p.divider("-");
    const lbl = Number(d.serviceFeeAmount) > 0 && Number(d.tip) > 0
      ? "Service fee + tip for"
      : Number(d.serviceFeeAmount) > 0 ? "Service fee for" : "Tip for";
    p.bold(true).row(`${lbl} ${d.gratuityRecipient}`, money(gratuityTotal)).bold(false);
  }

  // ── Footer ──
  p.feed(1).align("center").size(1, 2).line(d.footer || "Thank you!").size(1, 1);
  p.feed(1).line(POWERED_BY);
  p.feed(3);
  if (cut) p.cut();
  return p.bytes();
}

function _b64(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Print a receipt with the best available transport. Resolves to
 * `{ ok, method, error? }`; never throws so callers can toast the result.
 *   method: "bluetooth" | "windows-raw" | "android-system" | "browser"
 */
export async function printSlip(data) {
  const res = await _printSlipInner(data);
  if (res && res.ok === false) {
    // Session 47 — a slip that did not print is a live-service problem;
    // surface it in the platform error log.
    try {
      const { reportClientError } = await import("./crashReporter");
      reportClientError("print", new Error(`Print failed (${res.method}): ${res.error || "unknown"}`), { route: window.location.pathname });
    } catch (_) { /* noop */ }
  }
  return res;
}

async function _printSlipInner(data) {
  const cfg = getPrinterConfig();
  const payload = { ...(data || {}), footer: (data && data.footer) || "Thank you!", poweredBy: POWERED_BY };
  const copies = Math.min(Math.max(Number(cfg.copies) || 1, 1), 3);

  // 1. Android APK
  const cap = _capacitorPrinter();
  if (cap) {
    if (cfg.btAddress) {
      try {
        const bytes = buildEscPosReceipt(payload, { paperWidth: cfg.paperWidth, cut: cfg.cut !== false });
        for (let i = 0; i < copies; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await cap.printRaw({ address: cfg.btAddress, data: _b64(bytes) });
        }
        return { ok: true, method: "bluetooth" };
      } catch (e) {
        return { ok: false, method: "bluetooth", error: e?.message || String(e) };
      }
    }
    try {
      await cap.printHtml({ html: buildReceiptHtml(payload), jobName: `${payload.business?.name || "Receipt"} slip` });
      return { ok: true, method: "android-system" };
    } catch (e) {
      return { ok: false, method: "android-system", error: e?.message || String(e) };
    }
  }

  // 2. Windows EXE
  const invoke = await _tauri();
  if (invoke) {
    try {
      const bytes = buildEscPosReceipt(payload, { paperWidth: cfg.paperWidth, cut: cfg.cut !== false });
      for (let i = 0; i < copies; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await invoke("print_raw_receipt", { printerName: cfg.winPrinterName || null, data: Array.from(bytes) });
      }
      return { ok: true, method: "windows-raw" };
    } catch (e) {
      return { ok: false, method: "windows-raw", error: typeof e === "string" ? e : (e?.message || String(e)) };
    }
  }

  // 3. Browser / PWA
  try {
    await printReceipt(payload);
    return { ok: true, method: "browser" };
  } catch (e) {
    return { ok: false, method: "browser", error: e?.message || String(e) };
  }
}

/** Android: paired Bluetooth devices (asks for permission if needed). */
export async function listBluetoothPrinters() {
  const cap = _capacitorPrinter();
  if (!cap) return { available: false, enabled: false, devices: [] };
  const r = await cap.listPaired({});
  return { available: !!r?.available, enabled: !!r?.enabled, devices: Array.isArray(r?.devices) ? r.devices : [] };
}

/** Windows: installed printers + the OS default. */
export async function listWindowsPrinters() {
  const invoke = await _tauri();
  if (!invoke) return { default: null, printers: [] };
  const r = await invoke("list_printers");
  return { default: r?.default || null, printers: Array.isArray(r?.printers) ? r.printers : [] };
}

/** Small sample slip so operators can verify paper width / connection. */
export function buildTestReceipt(businessName, formatPrice) {
  return {
    business: { name: businessName || "NFC Solutions" },
    tableLabel: "Test slip",
    orderLabel: "Order TEST-0001",
    dateStr: new Date().toLocaleString(),
    lines: [
      { qty: 1, name: "Sample item", price: 45, modifiers: ["Side: Chips", "Doneness: Medium"] },
      { qty: 2, name: "Another item with a fairly long name", price: 24 },
    ],
    subtotal: 60,
    vatRate: 15,
    vatAmount: 9,
    total: 69,
    payments: [{ label: "Cash", amount: 100 }],
    changeDue: 31,
    footer: "Thank you!",
    formatPrice,
  };
}
